import apiService from './api';

interface Company {
  id: number;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  website: string;
  logo_url: string;
  subdomain?: string;
  is_active: boolean;
  storage_enabled?: boolean;
  created_at: string;
  updated_at: string;
}

interface CompanyStats {
  total_companies: number;
  active_companies: number;
  inactive_companies: number;
  total_employees: number;
  total_customers: number;
  total_invoices: number;
}

interface CompanyDetailStats {
  total_employees: number;
  total_customers: number;
  total_invoices: number;
  active_invoices: number;
}

interface CreateCompanyData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  website?: string;
  logo_url?: string;
  is_active?: boolean;
  storage_enabled?: boolean;
}

interface UpdateCompanyData extends CreateCompanyData {
  id: number;
}

interface CreateCompanyResponse {
  company: Company;
  admin: {
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
    password: string;
  };
  subdomain?: string;
}

class CompanyService {
  async getCompanies(): Promise<Company[]> {
    const response = await apiService.get<Company[]>('/companies');
    return response.data;
  }

  async getCompany(id: number): Promise<Company> {
    const response = await apiService.get<Company>(`/companies/${id}`);
    return response.data;
  }

  async createCompany(data: CreateCompanyData): Promise<CreateCompanyResponse> {
    const response = await apiService.post<CreateCompanyResponse>('/companies', data);
    return response.data;
  }

  async updateCompany(data: UpdateCompanyData): Promise<Company> {
    const response = await apiService.put<Company>(`/companies/${data.id}`, data);
    return response.data;
  }

  async deleteCompany(id: number): Promise<void> {
    await apiService.delete(`/companies/${id}`);
  }

  async toggleCompanyStatus(id: number, isActive: boolean): Promise<Company> {
    const response = await apiService.patch<Company>(`/companies/${id}/toggle-status`, {
      is_active: isActive
    });
    return response.data;
  }

  async getOverallStats(): Promise<CompanyStats> {
    const response = await apiService.get<CompanyStats>('/companies/stats/overall');
    return response.data;
  }

  async getCompanyStats(id: number): Promise<CompanyDetailStats> {
    const response = await apiService.get<CompanyDetailStats>(`/companies/${id}/stats`);
    return response.data;
  }

  async getActiveCompanies(): Promise<Company[]> {
    const response = await apiService.get<Company[]>('/companies');
    return response.data.filter(company => company.is_active);
  }

  async getInactiveCompanies(): Promise<Company[]> {
    const response = await apiService.get<Company[]>('/companies');
    return response.data.filter(company => !company.is_active);
  }
}

export const companyService = new CompanyService();
export default companyService; 